import { useNavigate } from '@tanstack/react-router';
import { ArrowUpRight, CircleDashed, SquareParking } from 'lucide-react';
import type { ParkingSpotListResponse } from '@/shared/api';
import { useObjectScope } from '@/shared/object-scope';

/** Ob der Gast im Portal einen Stellplatz dazubuchen kann — und warum nicht. */
export function parkingBookable(parking: ParkingSpotListResponse | undefined, failed = false) {
  const active = parking?.spots.filter((spot) => spot.isActive).length ?? 0;
  // Ein Ladefehler ist kein „keine Stellplätze“ — sonst sähe es aus, als gäbe es nichts zu buchen.
  if (failed && !parking) {
    return { active, bookable: false, reason: 'Stellplätze konnten nicht geladen werden' };
  }
  if (!parking) return { active, bookable: false, reason: 'Wird geladen …' };
  if (active === 0) return { active, bookable: false, reason: 'Keine aktiven Stellplätze' };
  if (!parking.paymentAvailable) {
    // Ein Platz ohne Preis braucht keine Bezahlung; nur die übrigen hängen an Stripe.
    const free = parking.spots.filter((spot) => spot.isActive && spot.price === 0).length;
    if (free === 0) {
      return {
        active,
        bookable: false,
        reason: 'Nicht buchbar – Zahlungen sind nicht eingerichtet',
      };
    }
    return {
      active,
      bookable: true,
      reason:
        free === active
          ? 'Im Portal kostenlos buchbar, solange ein Platz frei ist'
          : 'Nur kostenlose Stellplätze buchbar – Zahlungen sind nicht eingerichtet',
    };
  }
  // Eine Pauschale über null, aber unter dem Mindestbetrag lässt sich allein nie bezahlen.
  const sellable = parking.spots.some(
    (spot) =>
      spot.isActive &&
      !(spot.priceUnit === 'PerStay' && spot.price > 0 && spot.price < parking.minimumChargeAmount),
  );
  if (!sellable) {
    return { active, bookable: false, reason: 'Nicht buchbar – Pauschale unter dem Mindestbetrag' };
  }
  return { active, bookable: true, reason: 'Im Portal buchbar, solange ein Platz frei ist' };
}

/**
 * Die eigenen Stellplätze als Zusammenfassung. Angelegt und belegt werden sie auf der Seite
 * „Parkplätze“ — dorthin führt der Sprung, statt sie hier ein zweites Mal zu pflegen.
 */
export function ObjectParkingSummary({
  groupId,
  parking,
  failed,
  failedReason,
  onRetry,
}: {
  groupId: string;
  parking: ParkingSpotListResponse | undefined;
  failed: boolean;
  /** Der Grund des Ladefehlers (z. B. fehlendes Recht am Objekt) statt des allgemeinen Satzes. */
  failedReason?: string;
  /** Fehlt, wenn ein neuer Versuch dieselbe Antwort brächte. */
  onRetry?: () => void;
}) {
  const navigate = useNavigate();
  const { setGroupId } = useObjectScope();
  const loaded = parkingBookable(parking, failed);
  const state = failed && !parking && failedReason ? { ...loaded, reason: failedReason } : loaded;
  return (
    <section
      aria-labelledby="own-parking-title"
      className="border-line/50 bg-card shadow-card rounded-lg border"
    >
      <div className="border-line/60 flex flex-wrap items-start justify-between gap-3 border-b px-5 py-4">
        <div className="min-w-0">
          <h3 id="own-parking-title" className="text-ink font-serif text-lg">
            Eigene Stellplätze
          </h3>
          <p className="text-ink-soft mt-0.5 text-sm">
            Buchbar zu einem Aufenthalt. Stellplätze und Belegung pflegt die Seite „Parkplätze“.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setGroupId(groupId);
            void navigate({ to: '/parking' });
          }}
          className="text-sage focus-visible:outline-sage inline-flex min-h-11 items-center gap-1 px-1 text-sm font-semibold hover:underline focus-visible:outline focus-visible:outline-2"
        >
          Parkplätze verwalten <ArrowUpRight className="size-4" aria-hidden />
        </button>
      </div>
      <div className="@container grid gap-3 px-5 py-4 sm:grid-cols-2">
        <div className="border-line flex min-w-0 items-center gap-3 rounded-lg border bg-white px-3 py-2.5">
          <SquareParking className="text-ink size-5 shrink-0" aria-hidden />
          <p className="min-w-0">
            <span className="text-ink block font-serif text-xl tabular-nums">
              {parking ? state.active : '–'}
            </span>
            <span className="text-ink-soft text-xs">
              {state.active === 1 ? 'aktiver Stellplatz' : 'aktive Stellplätze'} am Objekt
            </span>
          </p>
        </div>
        <div className="border-line flex min-w-0 items-center gap-3 rounded-lg border bg-white px-3 py-2.5">
          <CircleDashed
            className={state.bookable ? 'text-sage size-5' : 'text-ink-faint size-5'}
            aria-hidden
          />
          <p className="text-ink min-w-0 text-sm font-semibold">
            {state.reason}
            {failed && !parking && onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="text-sage ml-2 min-h-11 font-semibold underline underline-offset-2"
              >
                Erneut laden
              </button>
            )}
          </p>
        </div>
      </div>
    </section>
  );
}
